import {
    Alert,
    Button,
    CircularProgress,
    Container
} from "@mui/material";

import Box from "@mui/material/Box";

import React, {
    useCallback,
    useEffect,
    useMemo,
    useState
} from "react";

import {
    CONFIGURATION_CATEGORY_URL,
    CONFIGURATION_ENVIRONMENT_URL,
    CONFIGURATION_RELATIONSHIP_URL,
    MAINTENANCE_WINDOW_URL,
    AFFECTED_SERVICES_URL,
    CHANGE_TYPE_URL

} from "../../Constants/Constants";

import AxiosInstance from "../../services/AxiosInstance";

import AppTableView from "../AppTableView";

import ConfigurationMasterDialog
    from "./ConfigurationMasterDialog";


function ConfigurationMaster({ type }) {

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);


    /*
     * IMPORTANT:
     * useMemo prevents config from being recreated
     * on every render.
     */
    const config = useMemo(() => {

        switch (type) {

            case "category":
                return {
                    title: "Configuration Category",
                    url: CONFIGURATION_CATEGORY_URL
                };

            case "environment":
                return {
                    title: "Configuration Environment",
                    url: CONFIGURATION_ENVIRONMENT_URL
                };

            case "relationship":
                return {
                    title: "Configuration Relationship",
                    url: CONFIGURATION_RELATIONSHIP_URL
                };

            case "maintenancewindow":
                return {
                    title: "Maintenance Window",
                    url: MAINTENANCE_WINDOW_URL
                };
            case "affectedservice":
                return {
                    title: "Affected Service",
                    url: AFFECTED_SERVICES_URL
                };
            case "changetype":
                return {
                    title: "Change Type",
                    url: CHANGE_TYPE_URL
                };


            default:
                return null;
        }

    }, [type]);


    const getData = useCallback(async () => {

        if (!config) {
            setError("Invalid configuration type.");
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);

        try {

            const response =
                await AxiosInstance.get(
                    config.url
                );

            setData(
                response.data || []
            );

        } catch (error) {

            console.error(
                `Error fetching ${config.title}:`,
                error
            );

            setError(
                `Failed to load ${config.title} data. Please try again.`
            );

        } finally {

            setLoading(false);
        }

    }, [config]);


    useEffect(() => {
        getData();
    }, [getData]);


    useEffect(() => {

        const handleConfigurationSaved = (event) => {

            if (event.detail?.type === type) {
                getData();
            }

        };

        window.addEventListener(
            "configuration-master-saved",
            handleConfigurationSaved
        );

        return () => {
            window.removeEventListener(
                "configuration-master-saved",
                handleConfigurationSaved
            );
        };

    }, [type, getData]);




    const columns = [
        {
            field: "id",
            flex: 1,
            headerName: "Id"
        },
        {
            field: "name",
            flex: 2,
            headerName: "Name"
        },
        {
            field: "enabled",
            flex: 1,
            headerName: "Enabled",

            renderCell: (params) =>
                params.value ? "YES" : "NO"
        },
        {
            field: "description",
            flex: 3,
            headerName: "Description"
        },
        {
            field: "action",
            flex: 1,
            headerName: "Actions",
            disableColumnMenu: true,
            sortable: false,

            renderCell: (params) => (

                <Box
                    sx={{
                        display: "flex",
                        gap: 1,
                        alignItems: "center"
                    }}
                >

                    <ConfigurationMasterDialog
                        configurationId={params.row.id}
                        configurationName="Edit"
                        variant="link"
                        type={type}
                        onSuccess={getData}
                    />

                </Box>
            )
        }
    ];


    if (error) {

        return (
            <Container>

                <Alert
                    severity="error"
                    action={
                        <Button
                            color="inherit"
                            size="small"
                            onClick={getData}
                        >
                            Retry
                        </Button>
                    }
                >
                    {error}
                </Alert>

            </Container>
        );
    }


    return (
        <Box
            sx={{
                borderRadius: "8px",
                overflow: "hidden",
                p: 2,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%"
            }}
        >

            {loading ? (

                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "100%"
                    }}
                >
                    <CircularProgress />
                </Box>

            ) : (

                <AppTableView
                    rows={data}
                    cols={columns}
                    initialPageSize={5}
                />

            )}

        </Box>
    );
}

export default ConfigurationMaster;